// @file Helper functions for billing components.
import { CancellationType, LibraryMembershipTier } from '@@/enums'
import type {
  Billing,
  BillingInformation,
  BillingPaid,
  LibraryBilling,
  LibraryBillingPaid,
  StairstepPriceTier,
} from '@@/types'
import type { Card } from '@padlet/arvo'

enum SwitchType {
  SwitchImmediately = 'switch_immediately',
  SwitchAtEndOfTerm = 'switch_at_end_of_term',
}

function normalizedPaymentSchedule(paymentSchedule: string): 'monthly' | 'annual' {
  return paymentSchedule === 'month' ? 'monthly' : 'annual'
}

function normalizedOppositePaymentSchedule(paymentSchedule: string): 'monthly' | 'annual' {
  return normalizedPaymentSchedule(paymentSchedule === 'month' ? 'year' : 'month')
}

/**
 * Request params for a cancel/downgrade call, with the optional survey answer attached.
 *
 * A blank answer is omitted entirely rather than sent as an empty string.
 */
function buildCancellationRequestParams(
  cancellationType: CancellationType,
  reasonText?: string,
): { cancellation_type: CancellationType; reason_text?: string } {
  const trimmedReasonText = reasonText?.trim()
  return {
    cancellation_type: cancellationType,
    ...(trimmedReasonText ? { reason_text: trimmedReasonText } : {}),
  }
}

function getCardInformationText(card: Partial<Card>): string {
  if (!card.type) return ''
  const cardType = card.type[0].toUpperCase() + card.type.slice(1)
  return card.last4 ? `${cardType} *${card.last4}` : cardType
}

function calculateLibraryPlanTotalPrice({ makersCount, price }: { makersCount: number; price: number }): number {
  return makersCount * price
}

function isPaidMembershipTier(tier: unknown): tier is BillingPaid['tier'] {
  return tier === 'silver' || tier === 'gold' || tier === 'platinum'
}

function isPaidBilling(billing: Billing | null): billing is BillingPaid {
  return isPaidMembershipTier(billing?.tier)
}

function isWebBilling(billing: Billing | null): billing is BillingPaid {
  return isPaidBilling(billing) && billing.payment_channel === 'chargebee'
}

function isPaidLibraryBilling(libraryBilling: LibraryBilling | null): libraryBilling is LibraryBillingPaid {
  return libraryBilling?.tier === LibraryMembershipTier.Gold
}

function getCurrentPlanTierPrice(stairstepTiers: StairstepPriceTier[] | undefined, currentTier: number): number {
  if (stairstepTiers == null || stairstepTiers.length === 0) {
    return 0
  }

  const lastTier = stairstepTiers[stairstepTiers.length - 1]

  if (currentTier >= lastTier.starting_unit) {
    return lastTier.formatted_price
  }

  return (
    stairstepTiers.find((tier) => currentTier >= tier.starting_unit && currentTier <= tier.ending_unit)
      ?.formatted_price ?? 0
  )
}

function isPaymentMethodPresent(card: Partial<Card> | null | undefined): boolean {
  if (card == null) return false
  return 'type' in card
}

function isBillingAddressPresent(address: BillingInformation['address'] | null | undefined): boolean {
  if (address == null) return false
  return Object.entries(address).some(([_key, value]) => value != null && String(value).length > 0)
}

/**
 * Returns a human readable version of the billing address, with the following format:
 *
 * <NAME>
 * <LINE1>, <LINE2>
 * <CITY>, <STATE> <POSTAL_CODE> <COUNTRY>
 * <COUNTRY>
 */
function getHumanReadableBillingInformation(billingInformation: BillingInformation): string {
  const {
    name,
    address: { line1, line2, postal_code, city, state, country },
  } = billingInformation
  const clearValue = (value?: string | null): string => (value ?? '').trim()

  const lines: string[] = []
  // Name
  lines.push(clearValue(name))

  // Address line
  lines.push([clearValue(line1), clearValue(line2)].filter(Boolean).join(', '))

  // Address region (city, state, postal code, country)
  const addressRegionLeft = clearValue(city)
  const addressRegionRight = [clearValue(state), clearValue(postal_code), clearValue(country)].filter(Boolean).join(' ')
  lines.push([addressRegionLeft, addressRegionRight].filter(Boolean).join(', '))

  return lines.filter((line: string) => line.trim().length !== 0).join('<br>')
}

export {
  buildCancellationRequestParams,
  calculateLibraryPlanTotalPrice,
  getCardInformationText,
  getCurrentPlanTierPrice,
  getHumanReadableBillingInformation,
  isBillingAddressPresent,
  isPaidBilling,
  isPaidMembershipTier,
  isPaidLibraryBilling,
  isPaymentMethodPresent,
  isWebBilling,
  normalizedOppositePaymentSchedule,
  normalizedPaymentSchedule,
  SwitchType,
}
